import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import html2pdf from 'html2pdf.js';
import { getMembres, getCotisations } from '../services/api';
import { notificationService } from '../utils/notifications';
import './ListeMontantsPrint.css';

const ListeMembresCotisationsPrint = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const printRef = useRef(null);
  const [membres, setMembres] = useState([]);
  const [cotisations, setCotisations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    association: '',
    commune: '',
    periodeType: 'mois',
    mois: 1,
    annee: new Date().getFullYear(),
    semaine: 1,
    statut: 'tous'
  });

  // Récupérer les paramètres depuis l'URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setFilters({
      association: params.get('association') || '',
      commune: params.get('commune') || '',
      periodeType: params.get('periodeType') || 'mois',
      mois: parseInt(params.get('mois')) || new Date().getMonth() + 1,
      annee: parseInt(params.get('annee')) || new Date().getFullYear(),
      semaine: parseInt(params.get('semaine')) || 1,
      statut: params.get('statut') || 'tous'
    });
  }, [location]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [membresRes, cotisationsRes] = await Promise.all([
          getMembres(),
          getCotisations()
        ]);
        setMembres(membresRes.data || []);
        setCotisations(cotisationsRes.data || []);
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error);
        notificationService.error('Erreur', 'Impossible de charger les données');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const handlePrint = () => {
    setTimeout(() => {
      window.print();
    }, 100);
  };

  const handleDownloadPDF = async () => {
    if (!printRef.current) return;

    try {
      const opt = {
        margin: [10, 10, 10, 10],
        filename: `Membres_Cotisations_${filters.association.replace(/\s+/g, '_')}_${filters.commune.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
          width: printRef.current.scrollWidth,
          height: printRef.current.scrollHeight,
          windowWidth: printRef.current.scrollWidth,
          allowTaint: true
        },
        jsPDF: { 
          unit: 'mm', 
          format: 'a4', 
          orientation: 'landscape'
        },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'], avoid: ['.print-header', '.print-table'] }
      };

      await html2pdf().set(opt).from(printRef.current).save();
      notificationService.success('PDF généré', 'Le fichier PDF a été téléchargé avec succès.');
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
      notificationService.error('Erreur', 'Erreur lors de la génération du PDF. Veuillez réessayer.');
    }
  };

  const handleClose = () => {
    window.close();
    if (!document.hidden) {
      navigate('/admin');
    }
  };

  // Filtrer les membres par association et commune
  const membresFiltres = membres.filter(membre => {
    if (filters.association && membre.association !== filters.association) return false;
    if (filters.commune && filters.commune !== 'toutes' && membre.commune !== filters.commune) return false;
    return true;
  });

  // Filtrer les cotisations
  const cotisationsFiltrees = cotisations.filter(cotisation => {
    // Vérifier que la cotisation appartient à un membre filtré
    const membre = membresFiltres.find(m => m.id === cotisation.membre_id);
    if (!membre) return false;

    // Filtrer par mois/année
    if (cotisation.mois !== filters.mois || cotisation.annee !== filters.annee) return false;

    // Filtrer par semaine si nécessaire
    if (filters.periodeType === 'semaine' && cotisation.semaine !== filters.semaine) return false;

    // Filtrer par statut
    if (filters.statut !== 'tous' && cotisation.statut !== filters.statut) return false;

    return true;
  });

  // Grouper les cotisations par membre
  const membresAvecCotisations = membresFiltres.map(membre => {
    const cotisationsMembre = cotisationsFiltrees.filter(c => c.membre_id === membre.id);
    return {
      membre,
      cotisations: cotisationsMembre
    };
  }).filter(item => item.cotisations.length > 0);

  const moisNoms = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
  const periodeText = filters.periodeType === 'mois' 
    ? `${moisNoms[filters.mois - 1]} ${filters.annee}`
    : `Semaine ${filters.semaine} - ${moisNoms[filters.mois - 1]} ${filters.annee}`;
  const statutText = filters.statut === 'tous' ? 'Tous' : filters.statut === 'paye' ? 'Payé' : 'Impayé';

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Chargement des données...</p>
      </div>
    );
  }

  return (
    <div className="liste-montants-print-page" style={{ display: 'block', visibility: 'visible' }}>
      <div className="print-toolbar" style={{ display: 'flex' }}>
        <div style={{ flex: 1 }}>
          <button className="btn btn-secondary" onClick={handleClose}>
            ✕ Fermer
          </button>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button className="btn btn-primary" onClick={handlePrint}>
            🖨️ Imprimer
          </button>
          <button className="btn btn-success" onClick={handleDownloadPDF}>
            📥 Télécharger PDF
          </button>
        </div>
      </div>

      <div ref={printRef} className="print-content" style={{ display: 'block', visibility: 'visible', background: 'white' }}>
        <div className="print-header">
          <h1>Liste des Membres avec leurs Cotisations</h1>
          <div style={{ marginTop: '15px' }}>
            <p className="print-date" style={{ marginTop: '5px' }}>
              <strong>Commune:</strong> {filters.commune === 'toutes' ? 'Toutes les communes' : (filters.commune || 'Toutes')}
            </p>
            <p className="print-date" style={{ marginTop: '5px' }}>
              <strong>Période:</strong> {periodeText}
            </p>
            <p className="print-date" style={{ marginTop: '5px' }}>
              <strong>Statut:</strong> {statutText}
            </p>
            <p className="print-date" style={{ marginTop: '5px' }}>
              <strong>Date d'édition:</strong> {new Date().toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>
        </div>

        <div className="print-table-container">
          <table className="print-table">
            <thead>
              <tr>
                <th>N°</th>
                <th>Matricule</th>
                <th>Nom complet</th>
                <th>Téléphone</th>
                <th>Détail cotisations (Semaine · Montant · Statut · Date paiement)</th>
                <th>Total payé (CDF)</th>
                <th>Total impayé (CDF)</th>
              </tr>
            </thead>
            <tbody>
              {membresAvecCotisations.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: 'center', padding: '20px' }}>
                    Aucune cotisation trouvée avec les critères sélectionnés
                  </td>
                </tr>
              ) : (
                membresAvecCotisations.map(({ membre, cotisations: cotisMembre }, index) => {
                  const totalPaye = cotisMembre.filter(c => c.statut === 'paye').reduce((s, c) => s + parseFloat(c.montant || 0), 0);
                  const totalImpaye = cotisMembre.filter(c => c.statut !== 'paye').reduce((s, c) => s + parseFloat(c.montant || 0), 0);
                  const detail = cotisMembre.map(c => `Sem. ${c.semaine}: ${parseFloat(c.montant || 0).toLocaleString('fr-FR')} CDF · ${c.statut === 'paye' ? 'Payé' : 'Impayé'} · ${c.date_paiement ? new Date(c.date_paiement).toLocaleDateString('fr-FR') : '-'}`).join(' ; ');
                  return (
                    <tr key={membre.id}>
                      <td>{index + 1}</td>
                      <td>{membre.numero_matricule || 'N/A'}</td>
                      <td>{`${membre.nom} ${membre.postnom || ''} ${membre.prenom}`.trim()}</td>
                      <td>{membre.telephone || 'N/A'}</td>
                      <td style={{ fontSize: '0.9em', whiteSpace: 'normal' }}>{detail}</td>
                      <td className="text-right">{totalPaye.toLocaleString('fr-FR', { maximumFractionDigits: 0 })}</td>
                      <td className="text-right">{totalImpaye.toLocaleString('fr-FR', { maximumFractionDigits: 0 })}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="print-footer">
          <p>Total membres: {membresAvecCotisations.length} | Total cotisations: {cotisationsFiltrees.length}</p>
        </div>
      </div>
    </div>
  );
};

export default ListeMembresCotisationsPrint;

